import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from "react-router-dom";
import useWidgetStore from '../store/useWidgetStore';
import useDataStore from '../store/useDataStore';
import dayjs from 'dayjs';
import 'dayjs/locale/nl';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import 'dayjs/locale/de';
import { useI18N } from '../context/I18NContext';
import { useFlow } from '../context/FlowContext';
import { useSettings } from '../context/SettingsContext';
import { apptypeNamesFor, capacityGroupNamesFrom, capacityLabelFor, resourceNamesFor } from '../helpers/appointment';
export default function BookingSummary() {
    const navigate = useNavigate();
    const [apptypeStr, setApptypeStr] = useState();
    const [resourceStr, setResourceStr] = useState();
    const [capacityStr, setCapacityStr] = useState();
    const [totalPriceStr, setTotalPriceStr] = useState();
    const [date, setDate] = useState();
    const { t, ts } = useI18N();
    const theme = useTheme();
    const flow = useFlow();
    const { showSelectedResource, staticData } = useSettings();
    const { booking, cart, enabled, language, setEnabled } = useWidgetStore();
    const { apptypes, resources } = useDataStore();
    const capacityGroupNames = useMemo(() => capacityGroupNamesFrom(flow.views), [flow.views]);
    useEffect(() => {
        setEnabled(Object.assign(Object.assign({}, enabled), { apptype: true, resource: true }));
    }, []);
    const [hasAppointmentProducts, setHasAppointmentProducts] = useState(false);
    useEffect(() => {
        setHasAppointmentProducts(cart.lines.filter((line) => line.origin === 'appointment').length > 0);
    }, [cart.lines]);
    useEffect(() => {
        if (booking.appointments) {
            // This bar shows just the first appointment as a reminder while the consumer is
            // elsewhere (filling in login/register details, say) - its own apptype/resource,
            // read off THIS appointment, not from the apptype/capacity pickers' live selection,
            // which by then may have moved on to a second appointment with a different apptype
            // and capacity entirely.
            let appointment = booking.appointments[0];
            setDate(dayjs(appointment.date).locale(language).format('dddd D MMMM HH:mm'));
            setApptypeStr(apptypeNamesFor(appointment, apptypes).join(', '));
            setResourceStr(resourceNamesFor(appointment, resources).join(', '));
            setCapacityStr(capacityLabelFor(appointment, capacityGroupNames, { t, ts }, 'summary'));
            if (appointment.price > 0) {
                let price = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(appointment.price).replace(',00', ',-');
                setTotalPriceStr(ts('Prijs: %price%', { price: price }, 'summary'));
            }
            else {
                setTotalPriceStr('');
            }
        }
    }, [apptypes, resources, booking, capacityGroupNames]);
    const goTo = (e) => {
        let target = e.target.closest('button');
        let path = target.getAttribute('rel');
        navigate(`/oa_widget_${path}`);
    };
    // console.log(cart.lines)
    return (_jsx(_Fragment, { children: hasAppointmentProducts && (_jsxs(Stack, { direction: 'row', justifyContent: "space-between", alignItems: "center", spacing: 2, sx: {
                background: theme.palette.background.default,
                color: theme.palette.text.primary,
                borderColor: theme.palette.divider,
                borderWidth: '0 0 1px 0',
                borderStyle: "solid",
                position: 'relative',
                padding: 1
            }, children: [_jsxs(Box, { sx: { paddingTop: 1 }, children: [_jsxs(Typography, { variant: "caption", sx: {
                                fontWeight: 500,
                                fontSize: '0.9rem'
                            }, children: [t('Je huidige selectie', 'nav'), ":"] }), _jsx(Typography, { variant: "subtitle2", sx: {
                                fontWeight: 700,
                                fontSize: '1.1rem',
                            }, children: date }), apptypeStr !== '' && (_jsx(Typography, { variant: "subtitle2", sx: {
                                fontWeight: 500,
                                fontSize: '1.1rem',
                            }, children: apptypeStr })), showSelectedResource && resourceStr !== '' && (_jsx(Typography, { variant: "subtitle2", sx: {
                                fontWeight: 500,
                                fontSize: '1.1rem',
                            }, children: resourceStr })), capacityStr !== '' && (_jsx(Typography, { variant: "subtitle2", sx: {
                                fontWeight: 500,
                                fontSize: '1.1rem',
                            }, children: capacityStr })), totalPriceStr !== '' && (_jsx(Typography, { variant: "subtitle2", sx: {
                                fontWeight: 500,
                                fontSize: '1.1rem',
                            }, children: totalPriceStr }))] }), _jsx(Box, { sx: { flexGrow: 1, textAlign: "right", alignContent: "space-around" }, children: _jsx(Button, { onClick: goTo, rel: 'index', variant: "contained", sx: { my: 1 }, children: t('Wijzigen', 'nav') }) })] })) }));
}
